
import { AlertCircle, LoaderCircle, Settings as SettingsIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import AppearanceSettingsForm from "../settings/AppearanceSettingsForm";
import GeneralSettingsForm from "../settings/GeneralSettingsForm";
import IntegrationStatusCards from "../settings/IntegrationStatusCards";
import MarketingSettingsForm from "../settings/MarketingSettingsForm";
import NotificationSettingsForm from "../settings/NotificationSettingsForm";
import OrderingSettingsForm from "../settings/OrderingSettingsForm";
import PaymentSettingsForm from "../settings/PaymentSettingsForm";
import PolicySettingsForm from "../settings/PolicySettingsForm";
import SettingsNavigation from "../settings/SettingsNavigation";
import StaffAccessSettings from "../settings/StaffAccessSettings";
import StorefrontSettingsForm from "../settings/StorefrontSettingsForm";
import SystemSettingsPanel from "../settings/SystemSettingsPanel";
import { fetchSettings, fetchSettingsHealth, updateGeneralSettings, updateMarketingSettings, updateNotificationSettings, updateOrderingSettings, updatePaymentSettings, updatePolicySettings, updateStorefrontSettings } from "../settings/settingsApi";

const tabs = [
  { id: "general", label: "General" },
  { id: "ordering", label: "Ordering" },
  { id: "payments", label: "Payments" },
  { id: "notifications", label: "Notifications" },
  { id: "marketing", label: "Marketing" },
  { id: "storefront", label: "Storefront" },
  { id: "policies", label: "Policies" },
  { id: "staff", label: "Staff & Access" },
  { id: "appearance", label: "Appearance" },
  { id: "system", label: "System" },
];

function normalizeSettings(value) {
  const settings = value || {};
  return {
    ...settings,
    general: settings.general || {},
    ordering: settings.ordering || {},
    payments: settings.payments || {},
    notifications: settings.notifications || {},
    marketing: settings.marketing || {},
    storefront: settings.storefront || {},
    policies: settings.policies || {},
    operations: settings.operations || {},
    features: settings.features || {},
    integrations: settings.integrations || {},
    delivery_summary: settings.delivery_summary || {},
  };
}

function SummaryLinks({ settings, navigate }) {
  return <div className="settings-linked-grid"><article><span>Delivery Settings</span><strong>{settings?.delivery_summary?.service_area_name}</strong><p>{settings?.delivery_summary?.minimum_notice_days} day notice is the checkout source of truth.</p><button type="button" className="btn btn--secondary" onClick={() => navigate("/admin/deliveries")}>Manage Delivery Settings</button></article></div>;
}

export default function AdminSettingsPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState("general");
  const [settings, setSettings] = useState(null);
  const [health, setHealth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState({});
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setIsLoading(true); setError("");
    try { const [settingsData, healthData] = await Promise.all([fetchSettings(), fetchSettingsHealth()]); setSettings(normalizeSettings(settingsData)); setHealth(healthData); }
    catch (err) { setError(err.message || "Settings could not be loaded."); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(section, fn, payload) {
    setStatus((current) => ({ ...current, [section]: "Saving..." }));
    try { const updated = await fn(payload); setSettings(normalizeSettings(await fetchSettings())); setStatus((current) => ({ ...current, [section]: "Saved" })); return updated; }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.error(`Settings ${section} save failed`, err);
      setStatus((current) => ({ ...current, [section]: err.message || "Settings could not be saved." }));
      return null;
    }
  }

  async function runHealth() { setHealth(await fetchSettingsHealth()); }
  async function disableOrdering() { if (window.confirm("Disable new online orders?")) await save("system", updateOrderingSettings, { accepting_orders: false, orders_closed_message: settings?.ordering?.orders_closed_message || "Online ordering is currently closed." }); }
  async function enableMaintenance() { if (window.confirm("Enable storefront maintenance mode?")) await save("system", updateStorefrontSettings, { maintenance_mode: true, maintenance_message: settings?.storefront?.maintenance_message || "The storefront is temporarily unavailable." }); }

  function renderTab() {
    if (!settings) return null;
    if (active === "general") return <><GeneralSettingsForm data={settings.general} status={status.general} onSave={(p)=>save("general", updateGeneralSettings, p)} /><SummaryLinks settings={settings} navigate={navigate}/></>;
    if (active === "ordering") return <OrderingSettingsForm data={settings.ordering} delivery={settings.delivery_summary} status={status.ordering} onSave={(p)=>save("ordering", updateOrderingSettings, p)} />;
    if (active === "payments") return <PaymentSettingsForm data={settings.payments} integrations={settings.integrations} status={status.payments} onSave={(p)=>save("payments", updatePaymentSettings, p)} />;
    if (active === "notifications") return <NotificationSettingsForm data={settings.notifications} status={status.notifications} onSave={(p)=>save("notifications", updateNotificationSettings, p)} />;
    if (active === "marketing") return <MarketingSettingsForm data={settings.marketing} status={status.marketing} onSave={(p)=>save("marketing", updateMarketingSettings, p)} />;
    if (active === "storefront") return <StorefrontSettingsForm data={settings.storefront} status={status.storefront} onSave={(p)=>save("storefront", updateStorefrontSettings, p)} />;
    if (active === "policies") return <PolicySettingsForm data={settings.policies} status={status.policies} onSave={(p)=>save("policies", updatePolicySettings, p)} />;
    if (active === "staff") return <StaffAccessSettings />;
    if (active === "appearance") return <AppearanceSettingsForm general={settings.general} />;
    return <SystemSettingsPanel health={health} settings={settings} onHealth={runHealth} onDisableOrdering={disableOrdering} onMaintenance={enableMaintenance} />;
  }

  return <AdminLayout><section className="admin-page settings-page"><header className="admin-page-heading"><div><span className="admin-eyebrow">Business Controls</span><h1>Settings</h1><p>Manage SugaLumps identity, checkout rules, payments, notifications, marketing copy, storefront visibility, policies, and feature access.</p></div><SettingsIcon size={34}/></header>{error && <div className="admin-menu-error"><AlertCircle size={18}/><span>{error}</span></div>}{isLoading ? <div className="inventory-loading"><LoaderCircle size={22} className="admin-spin"/> Loading settings...</div> : <><IntegrationStatusCards settings={settings} health={health}/><SettingsNavigation tabs={tabs} active={active} onChange={setActive}/>{renderTab()}</>}</section></AdminLayout>;
}
